import { Authentication, Domain, Hooks } from '@3nickels/data-modules';
import { useService } from '@aesop-fables/containr-react';
import { useObservable } from '@guidedchoice/scrinium';
import { useEffect, useState } from 'react';
import { AuthLocalStorage } from '../authentication';

export * from './useSubmitReady';
export * from './useLocalStorage';

export function useAuthenticationContext(): Authentication.IAuthenticationContext {
  return useService<Authentication.IAuthenticationContext>(
    Authentication.AuthenticationServices.Context
  );
}

export function useAuthenticatedFlag(): boolean {
  const context = useAuthenticationContext();
  return useObservable(context.isAuthenticated$) ?? false;
}

export function useAuthenticationReady(): boolean | undefined {
  const context = useAuthenticationContext();
  return useObservable(context.authReady$);
}

interface AuthenticationStatus {
  isSessionExpired: boolean;
  isReady: boolean;
}

export function useAuthenticationStatus(): AuthenticationStatus {
  const authReady = useAuthenticationReady();
  const isAuthenticated = useAuthenticatedFlag();
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [readyTimedOut, setReadyTimedOut] = useState(false);

  useEffect(() => {
    if (authReady === true) {
      setReadyTimedOut(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setReadyTimedOut(true);
      console.warn('[auth] authReady timed out; proceeding with fallback readiness', {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
      });
    }, 8000);

    return () => clearTimeout(timeoutId);
  }, [authReady]);

  useEffect(() => {
    if (!(authReady === true || readyTimedOut)) return;

    let cache = false;
    try {
      cache = AuthLocalStorage.isAuthenticated();
    } catch (error) {
      console.warn('[auth] localStorage unavailable while checking session', error);
    }

    setIsSessionExpired(!isAuthenticated && cache);
  }, [authReady, isAuthenticated, readyTimedOut]);

  return {
    isSessionExpired,
    isReady: authReady === true || readyTimedOut,
  };
}

export function useNeedsOnboarding() {
  const userConfig = Hooks.useUserConfig();
  const productType = Hooks.useProductType();

  if (typeof userConfig === 'undefined' || typeof productType === 'undefined') return undefined;

  return productType === Domain.ProductTypeEnum.advice && !userConfig?.adviceOnboarded;
}

export function useIsFreeUser() {
  const productType = Hooks.useProductType();

  return productType === Domain.ProductTypeEnum.free;
}
